import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import TrueImpactSimpleModal from '../../design-system/components/TrueImpactSimpleModal';
import { Report } from './utils';
import { useGraphQLRequest } from '../../auth/hooks/useApiRequest';
import { useClient } from '../../apollo';
import {
  getReportVisibility,
  MAX_WIDTH_REPORT_VISIBILITY_OPTION,
  ReportVisibilityOptions,
  ReportVisibilityRadioOptions,
  SharingType,
} from 'shared/account/pages/reportSharing/ReportSharingOptions';
import { SPACERS } from '../../design-system/settings/variables/Spacers';
import TrueImpactText from '../../design-system/base/TrueImpactText';
import TrueImpactFloatLoading from '../../design-system/components/TrueImpactFloatLoading';
import { updateReportVisibility } from 'shared/account/pages/reportSharing/api';

export const publicVisibilityOption: ReportVisibilityOptions = {
  value: 'public',
  heading: 'Public (Recommended)',
  subHeading:
    'Expand your reach with no additional work. Your report will be shared across our trusted network of platforms, ' +
    'helping funders and donors discover your organization and understand your impact.',
};

export const privateVisibilityOption: ReportVisibilityOptions = {
  value: 'private',
  heading: 'Restricted',
  subHeading:
    'Limit visibility to this specific report. Choose this option if the report is tailored for a particular funder.',
};

const defaultOptions: ReportVisibilityOptions[] = [
  publicVisibilityOption,
  privateVisibilityOption,
];

export const donorNamePublicDisabledReason =
  'Public is unavailable because this report mentions a funder by name. '

const donorNameOptions: ReportVisibilityOptions[] = [
  {
    ...publicVisibilityOption,
    disabled: true,
    disabledReason: donorNamePublicDisabledReason,
  },
  privateVisibilityOption,
];

interface ReportVisibilityModalProps {
  open: boolean;
  report: Report;
  value: SharingType;
  onClose: (value: SharingType) => unknown;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.extraLarge} ${SPACERS.extraLarge} ${SPACERS.medium}
    ${SPACERS.extraLarge};
  margin-left: auto;
  margin-right: auto;
  max-width: ${MAX_WIDTH_REPORT_VISIBILITY_OPTION + 60}px;
  .report-visibility-text {
    margin-bottom: ${SPACERS.small};
  }
`;

const noOp = () => null;

export const ReportVisibilityOptionsRender: React.FC<Omit<
  ReportVisibilityModalProps,
  'open'
> & { options?: ReportVisibilityOptions[] }> = ({
  report,
  value,
  onClose,
  options,
}) => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest();
  // A report flagged as naming a funder cannot be made public (the backend
  // enforces it too), so never offer the option.
  const hasDonorNameFlag = Boolean(report.level1DonorName);
  const visibilityOptions = useMemo(
    () => options || (hasDonorNameFlag ? donorNameOptions : defaultOptions),
    [options, hasDonorNameFlag],
  );

  const handleSave = useCallback(
    (value: SharingType) => {
      if (!client) return;
      const request = updateReportVisibility(client, {
        reportId: report.dbId,
        visibility: getReportVisibility(value),
      });
      makeRequest(request);
      request.then(() => {
        onClose(value);
      });
    },
    [onClose],
  );

  return (
    <>
      <TrueImpactFloatLoading
        requestState={requestState}
        loadingMessage="Saving report's visibility"
        successMessage="Report's visibility saved successfully"
        errorMessage="Failed to save report's visibility"
      />
      <ReportVisibilityRadioOptions
        options={visibilityOptions}
        subSelection={hasDonorNameFlag ? 'private' : value}
        setSubSelection={handleSave}
        disabled={requestState.type === 'REQUEST_START'}
      />
      <TrueImpactText
        style={{
          fontStyle: 'italic',
          marginTop: SPACERS.small,
        }}
        className="visibility-note-text"
      >
        Note that both options will still allow the report to be shared with
        your specific funder if allocating a donation to it.
      </TrueImpactText>
    </>
  );
};

const ReportVisibilityModal: React.FC<ReportVisibilityModalProps> = ({
  open,
  report,
  value,
  onClose,
}) => {
  return (
    <TrueImpactSimpleModal
      open={open}
      onClose={noOp}
      next={{
        text: 'Done',
        onClick: () => onClose(value),
      }}
      contentStyle={{ borderRadius: 4 }}
      footerStyle={{ borderBottomLeftRadius: 4, borderBottomRightRadius: 4 }}
    >
      <StyledDiv>
        <TrueImpactText className="report-visibility-text">
          True Impact now gives you the option to make your impact reports
          <br />
          publicly accessible through the True Impact reporting network.
        </TrueImpactText>
        <ReportVisibilityOptionsRender
          report={report}
          value={value}
          onClose={onClose}
        />
      </StyledDiv>
    </TrueImpactSimpleModal>
  );
};

export default ReportVisibilityModal;
